import { t } from '@lingui/macro'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { Card } from '@/@bothrs-ui/web/components/card/Card'
import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { IconButton } from '@/@bothrs-ui/web/components/primitives/icon-button/IconButton'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H2, H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { mixpanel } from '@/mixpanel'
import { BookingFlowActions, BookingFlowCenteredContent, BookingFlowLayout } from './BookingFlowLayout'

import type { FlowState } from '../types/booking.type'

type OtherDepartmentData = {
  departmentName: string
  phoneNumber?: string
  phoneNumberDisplay?: string
  websiteUrl?: string
}

type OtherDepartmentContentProps = {
  flowState: FlowState
  data: OtherDepartmentData
  isLoading?: boolean
  title?: string
  description?: string
}

export const OtherDepartmentContent = ({
  flowState,
  data,
  isLoading,
  title,
  description,
}: OtherDepartmentContentProps) => {
  const theme = useTheme()
  const navigate = useNavigate()

  const handleClose = () => {
    mixpanel.track('booking_flow_closed', {
      step: 'other-department',
      departmentName: data.departmentName,
      withTriage: !!flowState.triagePath?.length,
    })
    navigate('/home', { replace: true })
  }

  const handlePhoneClick = () => {
    mixpanel.track('booking_flow_other_department_phone_clicked', {
      phoneNumber: data.phoneNumber,
      departmentName: data.departmentName,
    })
    window.open(`tel:${data.phoneNumber}`, '_self')
  }

  const handleWebsiteClick = () => {
    // Only allow http(s) schemes to prevent XSS via javascript: URLs
    try {
      const parsed = new URL(data.websiteUrl!, window.location.origin)
      if (!['http:', 'https:'].includes(parsed.protocol)) return
    } catch {
      return
    }

    mixpanel.track('booking_flow_other_department_website_clicked', {
      websiteUrl: data.websiteUrl,
      departmentName: data.departmentName,
    })
    window.open(data.websiteUrl, '_blank')
  }

  const hospitalName = t({
    id: 'appointments.book.other_department.hospital_name',
    comment: 'Hospital name shown on other department end screen',
    message: 'Maria Middelares met sites in Gent, Deinze, Aalter en Gentbrugge',
  })

  return (
    <BookingFlowLayout>
      <BookingFlowCenteredContent>
        <H2 noMargin data-testid='other-department-end-title'>
          {title ||
            t({
              id: 'appointments.book.other_department.title',
              comment: 'Other department end screen title',
              message: 'Neem contact op met de dienst {departmentName}',
              values: { departmentName: data.departmentName },
            })}
        </H2>
        <Span type='BodyMediumRegular'>
          {description ||
            t({
              id: 'appointments.book.other_department.description',
              comment: 'Other department end screen description',
              message:
                'Wegens de aard van je zorgvraag is het beter dat je telefonisch contact opneemt met {departmentName}.',
              values: { departmentName: data.departmentName },
            })}
        </Span>

        {data.phoneNumber && (
          <Card>
            <FlexColumn gap={theme.UI.SpacingPx.Space3}>
              <FlexColumn gap={theme.UI.SpacingPx.Space1}>
                <H3 noMargin>{hospitalName}</H3>
                <Span type='BodySmallRegular' color={theme.Colors.Neutrals.Darker}>
                  {t({
                    id: 'appointments.book.other_department.secretariat',
                    comment: 'Secretariat label for other department',
                    message: 'Secretariaat {departmentName}',
                    values: { departmentName: data.departmentName },
                  })}
                </Span>
              </FlexColumn>
              <Line />
              <FlexRow justifyContent='space-between' alignItems='center'>
                <Span type='BodyMediumRegular'>{data.phoneNumberDisplay || data.phoneNumber}</Span>
                <IconButton variant='Secondary' icon={['fal', 'phone']} size={'small'} onClick={handlePhoneClick} />
              </FlexRow>
            </FlexColumn>
          </Card>
        )}

        {data.websiteUrl && (
          <Card>
            <FlexColumn gap={theme.UI.SpacingPx.Space3}>
              <FlexColumn gap={theme.UI.SpacingPx.Space1}>
                <H3 noMargin>{hospitalName}</H3>
                <Span type='BodySmallRegular' color={theme.Colors.Neutrals.Darker}>
                  {data.departmentName}
                </Span>
              </FlexColumn>
              <Line />
              <FlexRow justifyContent='space-between' alignItems='center'>
                <Span type='BodyMediumRegular'>
                  {t({
                    id: 'appointments.book.other_department.website_label',
                    comment: 'Website link label',
                    message: 'Klik op de link:',
                  })}
                </Span>
                <IconButton variant='Secondary' icon={['fal', 'globe']} size={'small'} onClick={handleWebsiteClick} />
              </FlexRow>
            </FlexColumn>
          </Card>
        )}
      </BookingFlowCenteredContent>

      <BookingFlowActions>
        <Button variant='Primary' onClick={handleClose} disabled={isLoading} isLoading={isLoading}>
          {t({ id: 'globals.close', message: 'Sluiten' })}
        </Button>
      </BookingFlowActions>
    </BookingFlowLayout>
  )
}
